import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup, Card, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import config from "../../../../config";
import { candidateStatusOptions } from "../../../../hooks/useRecruitmentCandidates";
import {
  createCandidateDocument,
  fetchCandidateDetail,
  updateCandidateStatus,
} from "../../../../services/recruitmentService";
import { decrypt } from "../../../../utils/crypto";
import { companyID } from "../../../../auth";
import api from "../../../../api/axios";
import { getAuthToken } from "../../../../utils/tokenStorage";
import TooltipWrapper from "../../../../utils/TooltipWrapper";
import CandidateStatusBadge from "./components/CandidateStatusBadge";
import CandidateTimeline from "./components/CandidateTimeline";
import CreateDocumentModal from "./components/CreateDocumentModal";
import SendDocumentModal from "./components/SendDocumentModal";
import HireCandidateModal from "./components/HireCandidateModal";
import Loading from "../../../../components/Loading";

const CandidateDetail = () => {
  const { candidate_id } = useParams();
  const decryptedId = useMemo(() => {
    try {
      return decrypt(candidate_id);
    } catch (error) {
      return candidate_id;
    }
  }, [candidate_id]);
  const { t } = useTranslation();
  const companyId = companyID();

  const [candidate, setCandidate] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [documentProcessing, setDocumentProcessing] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendProcessing, setSendProcessing] = useState(false);
  const [hireModalOpen, setHireModalOpen] = useState(false);
  const [hireProcessing, setHireProcessing] = useState(false);

  const resumeUrl = useMemo(() => {
    if (!candidate) return null;
    if (candidate.resume_url) return candidate.resume_url;
    if (candidate.resume_path) return candidate.resume_path;
    if (candidate.resume) return candidate.resume;
    return null;
  }, [candidate]);

  const getAbsoluteResumeUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${config?.IMAGE_URL || ""}${url}`;
  };

  const loadCandidate = useCallback(async () => {
    if (!decryptedId) return;
    setLoading(true);
    try {
      const response = await fetchCandidateDetail(decryptedId);
      const details =
        response?.result || response?.data || response?.candidate || response;
      setCandidate(details);
      if (details?.activity_logs) {
        setTimeline(details.activity_logs);
      }
    } catch (error) {
      console.error("Error fetching candidate detail", error);
      toast.error(t("Unable to fetch candidate details"));
      setCandidate(null);
    } finally {
      setLoading(false);
    }
  }, [decryptedId, t]);

  useEffect(() => {
    loadCandidate();
  }, [loadCandidate]);

  const handleStatusChange = async (event) => {
    const newStatus = event.target.value;
    if (!newStatus || !decryptedId) return;

    // Show SweetAlert for note input
    const result = await Swal.fire({
      title: t("Change Candidate Status"),
      html: `
        <div class="text-start mb-3">
          <p class="mb-2"><strong>${t(
            "New Status"
          )}:</strong> <span class="badge bg-primary">${newStatus}</span></p>
          <p class="mb-0 text-muted">${t(
            "Add a note for this status change (optional)"
          )}</p>
        </div>
      `,
      input: "textarea",
      inputPlaceholder: t("Enter your note here..."),
      inputAttributes: {
        "aria-label": t("Status change note"),
        rows: 4,
        cols: 50,
      },
      showCancelButton: true,
      confirmButtonText: t("Update Status"),
      cancelButtonText: t("Cancel"),
      confirmButtonColor: "#007bff",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "swal-status-change",
        input: "swal-textarea-input",
      },
      width: "500px",
      preConfirm: (note) => {
        return note || ""; // Return empty string if note provided
      },
    });

    // If user cancelled, reset the select value
    if (result.isDismissed) {
      event.target.value = candidate.current_status || "";
      return;
    }

    setStatusUpdating(true);
    try {
      const note = result.value || `Status updated to ${newStatus}`;
      await updateCandidateStatus(decryptedId, newStatus, note);
      toast.success(t("Status updated successfully"));
      loadCandidate();
    } catch (error) {
      // Reset select value on error
      event.target.value = candidate.current_status || "";
      toast.error(t("Unable to update status"));
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCreateDocument = async (values) => {
    if (!decryptedId) return;
    setDocumentProcessing(true);
    try {
      await createCandidateDocument(decryptedId, values);
      toast.success(t("Document created successfully"));
      setDocumentModalOpen(false);
      loadCandidate();
    } catch (error) {
      console.error("Error creating document:", error);
      toast.error(t("Unable to create document"));
    } finally {
      setDocumentProcessing(false);
    }
  };

  const handleSendDocument = async (values) => {
    if (!decryptedId) return;
    setSendProcessing(true);

    try {
      const payload = {
        subject: values.subject,
        content: values.content,
        documents: values.documents.map((doc) => ({
          id: doc.id,
          title: doc.title,
          type: doc.type,
          format: doc.format,
          created_at: doc.created_at,
          url: doc.url,
        })),
        template_id: values.template_id || null,
        recipient_email: values.recipient_email || candidate?.email,
        recipient_name: values.recipient_name || candidate?.full_name,
      };
      const response = await api.post("/api/v2/documents/send-email", payload);
      if (response.data?.success) {
        toast.success(
          response.data?.message || t("Document sent successfully")
        );
      } else {
        toast.success(t("Document sent successfully"));
      }

      setSendModalOpen(false);
      loadCandidate();
    } catch (error) {
      console.error("Error sending document:", error);

      // Handle different error scenarios
      if (error.response?.status === 400) {
        toast.error(
          error.response?.data?.message || t("Invalid request data")
        );
      } else if (error.response?.status === 404) {
        toast.error(t("Document or candidate not found"));
      } else if (error.response?.status === 500) {
        toast.error(t("Server error. Please try again later"));
      } else {
        toast.error(
          error.response?.data?.message || t("Unable to send document")
        );
      }
    } finally {
      setSendProcessing(false);
    }
  };

  const handleResumeDownload = () => {
    const url = getAbsoluteResumeUrl(resumeUrl);
    if (!url) {
      toast.info(t("Resume not available"));
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleHireCandidate = async (employeeData) => {
    if (!decryptedId) return;
    setHireProcessing(true);

    try {
      // Step 1: Create employee basic info
      const basicFormData = new FormData();

      // Basic employee data
      basicFormData.append("first_name", employeeData.first_name);
      basicFormData.append("last_name", employeeData.last_name);
      basicFormData.append("middle_name", employeeData.middle_name || "");
      basicFormData.append("personal_email", employeeData.personal_email);
      basicFormData.append("official_email", employeeData.official_email);
      basicFormData.append("number", employeeData.number);
      basicFormData.append(
        "alternate_number",
        employeeData.alternate_number || ""
      );
      basicFormData.append("gender", employeeData.gender);
      basicFormData.append("date_of_birth", employeeData.date_of_birth);
      basicFormData.append("guardian_name", employeeData.guardian_name);
      basicFormData.append("nationality", employeeData.nationality);
      basicFormData.append("marital_status", employeeData.marital_status);
      basicFormData.append("company_id", companyId);

      console.log("Creating employee with basic info:", employeeData);

      const token = getAuthToken();

      let basicResponse;
      try {
        basicResponse = await api.post("employees", basicFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (apiError) {
        console.error("Error creating employee:", apiError);
        toast.error(t("Failed to create employee basic information"));
        return;
      }

      if (!basicResponse.data?.result?.insertId) {
        toast.error(t("Failed to create employee basic information"));
        return;
      }

      const employeeId = basicResponse.data.result.insertId;
      console.log("Employee created with ID:", employeeId);

      // Step 2: Create employment details
      const employmentFormData = new FormData();
      employmentFormData.append("employee_id", employeeId);
      employmentFormData.append("employment_id", employeeData.employment_id);
      employmentFormData.append("department_id", employeeData.department_id);
      employmentFormData.append("designation_id", employeeData.designation_id);
      employmentFormData.append("joining_date", employeeData.joining_date);
      employmentFormData.append(
        "employment_type",
        employeeData.employment_type
      );
      employmentFormData.append(
        "employment_status",
        employeeData.employment_status
      );
      employmentFormData.append(
        "probation_end_date",
        employeeData.probation_end_date
      );
      employmentFormData.append("salary", employeeData.salary);
      employmentFormData.append(
        "hourly_rate",
        employeeData.employment_type === "hourly"
          ? employeeData.hourly_rate ?? ""
          : ""
      );
      employmentFormData.append("role_id", employeeData.role_id);

      // Handle reporter_id - extract employee_id if it's in display format

      employmentFormData.append("reporter_id", employeeData.reporter_id || "");

      // Handle work_location and work_shift
      employmentFormData.append("work_location", employeeData.work_mode || "");
      employmentFormData.append("work_shift", employeeData.working_shift || "");

      let employmentResponse;
      try {
        employmentResponse = await api.post(
          "employees/employment_details",
          employmentFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (apiError) {
        console.error("Error creating employment details:", apiError);
        toast.error(t("Failed to create employment details"));
        return;
      }
      if (!employmentResponse.data?.success) {
        toast.error(t("Failed to create employment details"));
        return;
      }
      // Step 3: Update candidate status to HIRED
      try {
        await updateCandidateStatus(
          decryptedId,
          "HIRED",
          "Candidate hired and converted to employee"
        );
      } catch (statusError) {
        console.error("Error updating candidate status:", statusError);
        toast.error(t("Failed to update candidate status to HIRED"));
        return;
      }

      toast.success(
        t("Candidate hired successfully and converted to employee!")
      );
      setHireModalOpen(false);

      // Refresh candidate data
      try {
        await loadCandidate();
      } catch (loadError) {
        console.error("Error refreshing candidate data:", loadError);
        // Don't show error for this as the main operation succeeded
      }
    } catch (error) {
      console.error("Error hiring candidate:", error);

      // Handle specific error messages
      let errorMessage = t("Failed to hire candidate");

      if (error.response?.status === 409) {
        errorMessage = error.response.data.message || t("Email already exists");
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setHireProcessing(false);
    }
  };

  // Filter out unwanted status options but keep HIRED
  const filteredStatusOptions = useMemo(
    () =>
      candidateStatusOptions.filter(
        (option) =>
          !["DOCUMENTS_SENT", "OFFER_ACCEPTED", "APPOINTMENT"].includes(
            option.value
          )
      ),
    []
  );

  if (loading) {
    return <Loading size="xl" variant="dots" />;
  }

  return (
    <div className="page-wrapper">
      <style>{`
        .swal-wide {
          width: 600px !important;
        }
        .swal2-textarea {
          border: 1px solid #ced4da !important;
          border-radius: 0.375rem !important;
          font-family: inherit !important;
        }
        .swal2-textarea:focus {
          border-color: #86b7fe !important;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
        }
      `}</style>
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={t("Recruitment")}
          title={t("Dashboard")}
          subtitle={t("Candidate Details")}
          name={t("Candidate Details")}
          page="recruitment"
        />
        {candidate ? (
          <>
            <Row className="g-4">
              <Col lg={8}>
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <h4 className="card-title mb-1">{candidate.full_name}</h4>
                      <div className="text-muted small">{candidate.email}</div>
                      <div className="text-muted small">
                        ID: {candidate.candidate_code}
                      </div>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <CandidateStatusBadge status={candidate.current_status} />
                      <Form.Select
                        size="sm"
                        className="w-auto"
                        value={candidate.current_status || ""}
                        onChange={handleStatusChange}
                        disabled={statusUpdating}
                      >
                        <option value="" disabled>
                          {t("Update Status")}
                        </option>
                        {filteredStatusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.label)}
                          </option>
                        ))}
                      </Form.Select>

                      <ButtonGroup size="sm" className="ms-2">
                        <TooltipWrapper tooltipText={t("Create Document")}>
                          <Button
                            variant="outline-success"
                            onClick={() => setDocumentModalOpen(true)}
                          >
                            <i className="fa fa-plus" />
                          </Button>
                        </TooltipWrapper>
                        <TooltipWrapper tooltipText={t("Send Document")}>
                          <Button
                            variant="outline-primary"
                            onClick={() => setSendModalOpen(true)}
                          >
                            <i className="fa fa-paper-plane" />
                          </Button>
                        </TooltipWrapper>
                        {candidate.current_status !== "HIRED" && (
                          <TooltipWrapper tooltipText={t("Hire Candidate")}>
                            <Button
                              variant="outline-warning"
                              onClick={() => setHireModalOpen(true)}
                            >
                              <i className="fa fa-user-plus" />
                            </Button>
                          </TooltipWrapper>
                        )}
                        <TooltipWrapper tooltipText={t("Resume")}>
                          <Button
                            variant="outline-secondary"
                            onClick={handleResumeDownload}
                          >
                            <i className="fa fa-download" />
                          </Button>
                        </TooltipWrapper>
                      </ButtonGroup>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row className="gy-3">
                      <Col md={6}>
                        <div className="text-muted small">{t("Email")}</div>
                        <div className="fw-semibold">
                          {candidate.email || "-"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">{t("Phone")}</div>
                        <div className="fw-semibold">
                          {candidate.phone || "-"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">
                          {t("Alternate Phone")}
                        </div>
                        <div className="fw-semibold">
                          {candidate.alternate_phone || "-"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">{t("Gender")}</div>
                        <div className="fw-semibold">
                          {candidate.gender || "-"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">
                          {t("Date of Birth")}
                        </div>
                        <div className="fw-semibold">
                          {candidate.dob
                            ? new Date(candidate.dob).toLocaleDateString()
                            : "-"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">
                          {t("Experience")}
                        </div>
                        <div className="fw-semibold">
                          {candidate.experience_years || 0} {t("yrs")}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">
                          {t("Current CTC")}
                        </div>
                        <div className="fw-semibold">
                          ₹{candidate.current_ctc || "0"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">
                          {t("Expected CTC")}
                        </div>
                        <div className="fw-semibold">
                          ₹{candidate.expected_ctc || "0"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">
                          {t("Notice Period")}
                        </div>
                        <div className="fw-semibold">
                          {candidate.notice_period_days || 0} {t("days")}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">
                          {t("Registered On")}
                        </div>
                        <div className="fw-semibold">
                          {candidate.created_at
                            ? new Date(
                                candidate.created_at * 1000
                              ).toLocaleString()
                            : "-"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-muted small">
                          {t("Created By")}
                        </div>
                        <div className="fw-semibold">
                          {candidate.created_by_name || "-"}
                        </div>
                      </Col>
                      {/* <Col md={6}>
                        <div className="text-muted small">{t("Remarks")}</div>
                        <div className="fw-semibold">
                          {candidate.remarks || t("No remarks added")}
                        </div>
                      </Col> */}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                <Card>
                  <Card.Header className="bg-light">
                    <div className="d-flex align-items-center">
                      <i className="fa fa-history me-2 text-primary"></i>
                      <h5 className="card-title mb-0">
                        {t("Activity Timeline")}
                      </h5>
                    </div>
                  </Card.Header>
                  <Card.Body
                    style={{
                      maxHeight: "455px",
                      marginBottom: "10px",
                      overflowY: "auto",
                    }}
                  >
                    <CandidateTimeline events={timeline} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <div className="alert alert-warning">{t("Candidate not found")}</div>
        )}
      </div>

      <CreateDocumentModal
        show={documentModalOpen}
        onClose={() => setDocumentModalOpen(false)}
        candidateName={candidate?.full_name}
        candidateData={candidate}
        onAction={handleCreateDocument}
        submitting={documentProcessing}
      />

      <SendDocumentModal
        show={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        candidateName={candidate?.full_name}
        candidateEmail={candidate?.email}
        candidateId={decryptedId}
        candidateData={candidate}
        onSend={handleSendDocument}
        submitting={sendProcessing}
      />

      <HireCandidateModal
        show={hireModalOpen}
        onClose={() => setHireModalOpen(false)}
        candidate={candidate}
        onHire={handleHireCandidate}
        submitting={hireProcessing}
      />
    </div>
  );
};

export default CandidateDetail;
